import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, ProgressBar, Row, Spinner } from 'react-bootstrap';
import {
  areAllSelectableOptionsSelected,
  buildSkinMatchDemoAnswers,
  ensureSkinMatchDemoQuestionnaire,
  getDisplayLabels,
  getSelectableOptionValues,
  getSelectedRank,
  isMultiQuestion,
  isQuestionAnswered,
  isSelected,
  SKIN_MATCH_DEMO_RESPONSE_SOURCE,
  SKIN_MATCH_DEMO_SURVEY_UUID,
  SkinMatchSurveyAnswers,
  SkinMatchSurveyQuestion,
  skinMatchSurveyQuestions,
} from './skinMatchSurvey';
import {
  SkinAnalysisDemoAnswer,
  SkinAnalysisDemoClient,
  SkinAnalysisDemoResult,
  RoutineRecommendationItem,
} from '../../infrastructure/SkinAnalysisDemoClient';
import { captureGuides } from './photoGuides';
import { Questionnaire, QuestionnaireClient, QuestionnaireResponse } from '../../infrastructure/QuestionnaireClient';
import {
  buildHautResultPresentation,
  getPreferredOverlayBaseImage,
  HautFaceSkinPresentation,
  HautInsightItem,
  HautMetricOverlayImage,
  HautMetricPresentation,
  HautResultImage,
  HautScoreSignal,
} from './hautResultPresenter';

const buildFileName = (file?: File): string => file?.name || 'No file selected';
const getSelectedFile = (event: React.ChangeEvent<HTMLInputElement>): File | undefined =>
  event.currentTarget.files?.[0];

const optionButtonVariant = (selected: boolean) => (selected ? 'success' : 'outline-secondary');

const MetricCard = ({ label, value, score }: { label: string; value: string | number; score?: number }) => (
  <div className="border rounded-2 p-3 h-100 bg-white">
    <div className="text-secondary small mb-1">{label}</div>
    <div className="fw-semibold">{value}</div>
    {typeof score === 'number' && (
      <ProgressBar now={score} max={100} className="mt-3" style={{ height: 6 }} aria-label={`${label} score`} />
    )}
  </div>
);

const RecommendationList = ({ title, items }: { title: string; items: RoutineRecommendationItem[] }) => {
  if (items.length === 0) return null;

  return (
    <section className="mb-4">
      <h3 className="h6 mb-3">{title}</h3>
      <Row className="g-3">
        {items.map((item) => (
          <Col md={6} xl={3} key={`${title}-${item.step}-${item.productName}`}>
            <div className="border rounded-2 p-3 h-100 bg-white">
              <Badge bg="dark" className="mb-2">
                {item.step}
              </Badge>
              <div className="fw-semibold mb-1">{item.productName}</div>
              <div className="small text-secondary mb-2">{item.category}</div>
              <p className="small mb-0">{item.matchReason}</p>
            </div>
          </Col>
        ))}
      </Row>
    </section>
  );
};

const SIDE_ORDER = ['front', 'left', 'right'];

const getSideSortIndex = (side: string): number => {
  const index = SIDE_ORDER.indexOf(side);
  return index === -1 ? SIDE_ORDER.length : index;
};

const groupImagesBySide = (images: HautResultImage[]): Array<[string, HautResultImage[]]> => {
  const groups = images.reduce<Map<string, HautResultImage[]>>((currentGroups, image) => {
    currentGroups.set(image.side, [...(currentGroups.get(image.side) ?? []), image]);
    return currentGroups;
  }, new Map());

  return Array.from(groups.entries()).sort(([leftSide], [rightSide]) => {
    const sideDiff = getSideSortIndex(leftSide) - getSideSortIndex(rightSide);
    return sideDiff === 0 ? leftSide.localeCompare(rightSide) : sideDiff;
  });
};

const scoreVariant = (score: number): 'success' | 'warning' | 'danger' => {
  if (score >= 75) return 'success';
  if (score >= 50) return 'warning';
  return 'danger';
};

const gradeVariant = (grade: number): 'success' | 'warning' | 'danger' => {
  if (grade <= 2) return 'success';
  if (grade === 3) return 'warning';
  return 'danger';
};

const ResultImageTile = ({ image }: { image: HautResultImage }) => (
  <div className="border rounded-2 p-2 h-100 bg-white">
    <div
      className="bg-light rounded-2 d-flex align-items-center justify-content-center overflow-hidden mb-2"
      style={{ height: 220 }}
    >
      <img src={image.url} alt={image.label} className="w-100 h-100" style={{ objectFit: 'contain' }} />
    </div>
    <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
      <div className="small fw-semibold">{image.label}</div>
      <Badge bg="light" text="dark" className="border">
        {image.variant}
      </Badge>
    </div>
    <p className="small text-secondary mb-0">{image.description}</p>
  </div>
);

const OverlayImagePreview = ({
  predictedImages,
  overlayImage,
}: {
  predictedImages: HautResultImage[];
  overlayImage: HautMetricOverlayImage;
}) => {
  const baseImage = getPreferredOverlayBaseImage(predictedImages, overlayImage);

  return (
    <div className="border rounded-2 p-2 h-100 bg-white">
      <div
        className="bg-light rounded-2 position-relative d-flex align-items-center justify-content-center overflow-hidden mb-2"
        style={{ height: 220 }}
      >
        {baseImage && (
          <img
            src={baseImage.url}
            alt={`${overlayImage.metricLabel} ${baseImage.label} base`}
            className="w-100 h-100"
            style={{ objectFit: 'contain' }}
          />
        )}
        <img
          src={overlayImage.url}
          alt={overlayImage.label}
          className={baseImage ? 'position-absolute top-0 start-0 w-100 h-100' : 'w-100 h-100'}
          style={{ objectFit: 'contain', opacity: baseImage ? 0.78 : 1 }}
        />
      </div>
      <div className="d-flex flex-wrap gap-1 mb-1">
        <Badge bg="secondary">{overlayImage.side}</Badge>
        <Badge bg="light" text="dark" className="border">
          {overlayImage.variant}
        </Badge>
      </div>
      <div className="small text-secondary">
        Base: {baseImage ? baseImage.label : 'No matching base image returned'}
      </div>
    </div>
  );
};

const HautMetricCard = ({
  metric,
  predictedImages,
}: {
  metric: HautMetricPresentation;
  predictedImages: HautResultImage[];
}) => {
  const clampedScore = typeof metric.score === 'number' ? Math.max(0, Math.min(100, metric.score)) : undefined;

  return (
    <div className="border rounded-2 p-3 bg-white h-100">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <div>
          <h3 className="h6 mb-1">{metric.label}</h3>
          <div className="small text-secondary font-monospace">{metric.key}</div>
        </div>
        <div className="d-flex flex-wrap gap-1 justify-content-end">
          {metric.tag && <Badge bg="dark">{metric.tag}</Badge>}
          {typeof metric.grade === 'number' && <Badge bg={gradeVariant(metric.grade)}>Grade {metric.grade}</Badge>}
        </div>
      </div>

      {typeof clampedScore === 'number' && (
        <div className="mb-3">
          <div className="d-flex justify-content-between small mb-1">
            <span className="text-secondary">Score</span>
            <span className="fw-semibold">{metric.score}</span>
          </div>
          <ProgressBar
            now={clampedScore}
            max={100}
            variant={scoreVariant(clampedScore)}
            style={{ height: 7 }}
            aria-label={`${metric.label} score`}
          />
        </div>
      )}

      {metric.gradeDescription && <p className="small text-secondary mb-2">{metric.gradeDescription}</p>}

      {metric.valueBadges.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mb-3">
          {metric.valueBadges.map((badge) => (
            <Badge bg="light" text="dark" className="border" key={`${metric.key}-${badge}`}>
              {badge}
            </Badge>
          ))}
        </div>
      )}

      {metric.maskImages.length > 0 ? (
        <Row className="g-2">
          {metric.maskImages.map((maskImage) => (
            <Col md={6} xl={4} key={`${metric.key}-${maskImage.side}-${maskImage.variant}-${maskImage.url}`}>
              <OverlayImagePreview predictedImages={predictedImages} overlayImage={maskImage} />
            </Col>
          ))}
        </Row>
      ) : (
        metric.emptyMaskReason && (
          <Alert variant="light" className="border mb-0 py-2 small">
            {metric.emptyMaskReason}
          </Alert>
        )
      )}
    </div>
  );
};

const HautSnapshotGrid = ({ items }: { items: HautInsightItem[] }) => {
  if (items.length === 0) return null;

  return (
    <section className="mb-4">
      <h3 className="h6 mb-2">Condition snapshot</h3>
      <Row className="g-2">
        {items.map((item) => (
          <Col md={4} key={`${item.label}-${item.value}`}>
            <div className="border rounded-2 p-3 bg-white h-100">
              <div className="small text-secondary mb-1">{item.label}</div>
              <div className="fw-semibold">{item.value}</div>
            </div>
          </Col>
        ))}
      </Row>
    </section>
  );
};

const HautScoreSignalList = ({ title, signals }: { title: string; signals: HautScoreSignal[] }) => {
  if (signals.length === 0) return null;

  return (
    <div className="border rounded-2 p-3 bg-white h-100">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <h3 className="h6 mb-0">{title}</h3>
        <Badge bg="light" text="dark" className="border">
          {signals.length}
        </Badge>
      </div>
      <div className="d-grid gap-3">
        {signals.map((signal) => {
          const clampedScore = Math.max(0, Math.min(100, signal.score));
          return (
            <div key={`${title}-${signal.key}`}>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <div className="small fw-semibold">{signal.label}</div>
                <div className="d-flex align-items-center gap-1">
                  {signal.tag && (
                    <Badge bg="secondary" className="fw-normal">
                      {signal.tag}
                    </Badge>
                  )}
                  <span className="small text-secondary">{signal.score}</span>
                </div>
              </div>
              <ProgressBar
                now={clampedScore}
                max={100}
                variant={scoreVariant(clampedScore)}
                style={{ height: 6 }}
                aria-label={`${title} ${signal.label} score`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HautFaceSkinResultSection = ({ faceSkin }: { faceSkin: HautFaceSkinPresentation }) => (
  <section className="mb-4">
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h2 className="h5 mb-1">Face Skin Analysis 3.0 details</h2>
            <p className="small text-secondary mb-0">
              Score is higher-is-better. Grade is lower-is-better: 1 is best and 5 is worst.
            </p>
          </div>
          {typeof faceSkin.allAlgorithmsCalculated === 'boolean' && (
            <Badge
              bg={faceSkin.allAlgorithmsCalculated ? 'success' : 'warning'}
              {...(!faceSkin.allAlgorithmsCalculated ? { text: 'dark' as const } : {})}
            >
              {faceSkin.allAlgorithmsCalculated ? 'Complete' : 'Pending'}
            </Badge>
          )}
        </div>

        {faceSkin.detectionErrors.length > 0 && (
          <Alert variant="warning">
            {faceSkin.detectionErrors.map((error) => (
              <div key={`${error.side}-${error.value}`}>
                {error.side}: {error.value}
              </div>
            ))}
          </Alert>
        )}

        <HautSnapshotGrid items={faceSkin.snapshot} />

        {(faceSkin.focusAreas.length > 0 || faceSkin.stableSignals.length > 0) && (
          <section className="mb-4">
            <Row className="g-3">
              <Col lg={6}>
                <HautScoreSignalList title="Focus areas" signals={faceSkin.focusAreas} />
              </Col>
              <Col lg={6}>
                <HautScoreSignalList title="Stable signals" signals={faceSkin.stableSignals} />
              </Col>
            </Row>
          </section>
        )}

        {faceSkin.predictedImages.length > 0 && (
          <section className="mb-4">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
              <h3 className="h6 mb-0">Base result images</h3>
              <Badge bg="light" text="dark" className="border">
                {faceSkin.predictedImages.length} images
              </Badge>
            </div>
            {groupImagesBySide(faceSkin.predictedImages).map(([side, images]) => (
              <div className="mb-3" key={side}>
                <div className="small text-secondary text-uppercase mb-2">{side}</div>
                <Row className="g-3">
                  {images.map((image) => (
                    <Col md={4} key={`${image.side}-${image.variant}-${image.url}`}>
                      <ResultImageTile image={image} />
                    </Col>
                  ))}
                </Row>
              </div>
            ))}
          </section>
        )}

        {faceSkin.metrics.length > 0 ? (
          <section>
            <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
              <h3 className="h6 mb-0">Skin parameters</h3>
              <Badge bg="light" text="dark" className="border">
                {faceSkin.metrics.length} fields
              </Badge>
            </div>
            <Row className="g-3">
              {faceSkin.metrics.map((metric) => (
                <Col lg={12} key={metric.key}>
                  <HautMetricCard metric={metric} predictedImages={faceSkin.predictedImages} />
                </Col>
              ))}
            </Row>
          </section>
        ) : (
          <Alert variant="warning" className="mb-0">
            Face Skin Analysis 3.0 parameters are not available in this response yet.
          </Alert>
        )}
      </Card.Body>
    </Card>
  </section>
);

export default function SkinAnalysisDemoPage() {
  const [faceImage, setFaceImage] = useState<File>();
  const [hairFrontImage, setHairFrontImage] = useState<File>();
  const [hairLeftImage, setHairLeftImage] = useState<File>();
  const [hairRightImage, setHairRightImage] = useState<File>();
  const [hairType, setHairType] = useState('Wavy');
  const [mainHairConcern, setMainHairConcern] = useState('Frizz');
  const [hasColoredHair, setHasColoredHair] = useState(true);
  const [hasHeatStyling, setHasHeatStyling] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<SkinMatchSurveyAnswers>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSurveyLoading, setIsSurveyLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [savedResponse, setSavedResponse] = useState<QuestionnaireResponse>();
  const [result, setResult] = useState<SkinAnalysisDemoResult>();

  const currentQuestion = skinMatchSurveyQuestions[currentQuestionIndex] ?? skinMatchSurveyQuestions[0]!;
  const isSingleQuestion = currentQuestion.type === 'single';
  const answeredQuestionCount = skinMatchSurveyQuestions.filter((question) =>
    isQuestionAnswered(question, quizAnswers),
  ).length;
  const quizProgress = Math.round((answeredQuestionCount / skinMatchSurveyQuestions.length) * 100);

  const answers = useMemo<SkinAnalysisDemoAnswer[]>(
    () =>
      buildSkinMatchDemoAnswers({
        quizAnswers,
        hairType,
        mainHairConcern,
        hasColoredHair,
        hasHeatStyling,
      }),
    [quizAnswers, hairType, mainHairConcern, hasColoredHair, hasHeatStyling],
  );
  const hautPresentation = useMemo(() => buildHautResultPresentation(result?.raw), [result?.raw]);

  useEffect(() => {
    let cancelled = false;

    const loadQuestionnaire = async () => {
      setIsSurveyLoading(true);
      setErrorMessage(undefined);
      try {
        const client = new QuestionnaireClient();
        const ensuredQuestionnaire = await ensureSkinMatchDemoQuestionnaire(client);
        if (!cancelled) {
          setQuestionnaire(ensuredQuestionnaire);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error instanceof Error ? error.message : 'Failed to initialize the demo survey.');
        }
      } finally {
        if (!cancelled) {
          setIsSurveyLoading(false);
        }
      }
    };

    void loadQuestionnaire();

    return () => {
      cancelled = true;
    };
  }, []);

  const updateSingleAnswer = (question: SkinMatchSurveyQuestion, optionValue: string) => {
    setQuizAnswers((currentAnswers) => ({ ...currentAnswers, [question.id]: optionValue }));
    setCurrentQuestionIndex((currentIndex) => Math.min(currentIndex + 1, skinMatchSurveyQuestions.length - 1));
  };

  const toggleMultiAnswer = (
    question: SkinMatchSurveyQuestion,
    optionValue: string,
    clearOthers: boolean | undefined,
  ) => {
    setQuizAnswers((currentAnswers) => {
      const currentValue = currentAnswers[question.id];
      const currentValues = Array.isArray(currentValue) ? currentValue : [];
      if (clearOthers) {
        return { ...currentAnswers, [question.id]: [optionValue] };
      }

      const clearValues = question.options.filter((option) => option.clearOthers).map((option) => option.value);
      const valuesWithoutClearOptions = currentValues.filter((value) => !clearValues.includes(value));
      const nextValues = valuesWithoutClearOptions.includes(optionValue)
        ? valuesWithoutClearOptions.filter((value) => value !== optionValue)
        : [...valuesWithoutClearOptions, optionValue];

      return { ...currentAnswers, [question.id]: nextValues };
    });
  };

  const toggleSelectAllAnswers = (question: SkinMatchSurveyQuestion) => {
    setQuizAnswers((currentAnswers) => {
      const selectableValues = getSelectableOptionValues(question);
      const hasAllSelected = areAllSelectableOptionsSelected(question, currentAnswers[question.id]);

      return {
        ...currentAnswers,
        [question.id]: hasAllSelected ? [] : selectableValues,
      };
    });
  };

  const goToPreviousQuestion = () => {
    setCurrentQuestionIndex((currentIndex) => Math.max(currentIndex - 1, 0));
  };

  const goToNextQuestion = () => {
    setCurrentQuestionIndex((currentIndex) => Math.min(currentIndex + 1, skinMatchSurveyQuestions.length - 1));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!faceImage) {
      setErrorMessage('Please upload the front face and hair photo first.');
      return;
    }
    if (!questionnaire?.latestVersion) {
      setErrorMessage('Skin Match survey is not ready yet.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(undefined);
    setSavedResponse(undefined);
    try {
      const questionnaireClient = new QuestionnaireClient();
      const responseRecord = await questionnaireClient.submitResponse(questionnaire.surveyUuid, {
        versionId: questionnaire.latestVersion.id,
        source: SKIN_MATCH_DEMO_RESPONSE_SOURCE,
        language: 'en',
        answers,
      });
      setSavedResponse(responseRecord);

      const skinAnalysisClient = new SkinAnalysisDemoClient();
      const response = await skinAnalysisClient.analyze({
        surveyUuid: questionnaire.surveyUuid,
        questionnaireVersionId: questionnaire.latestVersion.id,
        answers,
        faceImage,
        ...(hairFrontImage ? { hairFrontImage } : {}),
        ...(hairLeftImage ? { hairLeftImage } : {}),
        ...(hairRightImage ? { hairRightImage } : {}),
      });
      setResult(response);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to run demo analysis.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="py-4 px-4" style={{ maxWidth: 1440 }}>
      <div className="mb-4">
        <Badge bg="success" className="mb-2">
          Internal demo
        </Badge>
        <h1 className="h3 mb-2">Skin Match Demo</h1>
        <p className="text-secondary mb-0">
          Upload a selfie, add optional hair angles, and preview the combined skin, hair, and routine output.
        </p>
        <div className="small text-secondary mt-2">
          Survey: <span className="font-monospace">{questionnaire?.surveyUuid ?? SKIN_MATCH_DEMO_SURVEY_UUID}</span>
          {questionnaire?.latestVersion ? ` · version #${questionnaire.latestVersion.id}` : ''}
        </div>
      </div>

      <Row className="g-4">
        <Col xl={4}>
          <Form onSubmit={handleSubmit}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h2 className="h5 mb-3">Photo intake</h2>
                <Form.Group controlId="faceImage" className="mb-3">
                  <Form.Label>Front face and hair photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFaceImage(getSelectedFile(event))}
                  />
                  <Form.Text>{buildFileName(faceImage)}</Form.Text>
                </Form.Group>
                <Form.Group controlId="hairFrontImage" className="mb-3">
                  <Form.Label>Optional dedicated front hair photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setHairFrontImage(getSelectedFile(event))}
                  />
                  <Form.Text>{buildFileName(hairFrontImage)}</Form.Text>
                </Form.Group>
                <Form.Group controlId="hairLeftImage" className="mb-3">
                  <Form.Label>Optional left hair photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setHairLeftImage(getSelectedFile(event))}
                  />
                  <Form.Text>{buildFileName(hairLeftImage)}</Form.Text>
                </Form.Group>
                <Form.Group controlId="hairRightImage">
                  <Form.Label>Optional right hair photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setHairRightImage(getSelectedFile(event))}
                  />
                  <Form.Text>{buildFileName(hairRightImage)}</Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                  <div>
                    <h2 className="h5 mb-1">Skin Match quiz</h2>
                    <div className="small text-secondary">
                      {answeredQuestionCount}/{skinMatchSurveyQuestions.length} answers selected
                    </div>
                  </div>
                  <Badge bg="light" text="dark" className="border">
                    {questionnaire?.latestVersion ? `v${questionnaire.latestVersion.version}` : 'Loading'}
                  </Badge>
                </div>
                <ProgressBar
                  now={quizProgress}
                  max={100}
                  className="mb-3"
                  style={{ height: 6 }}
                  aria-label="Skin Match quiz progress"
                />
                <div className="border rounded-2 p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <span className="small text-secondary">
                      Question {currentQuestionIndex + 1} of {skinMatchSurveyQuestions.length}
                    </span>
                    <Badge bg={isMultiQuestion(currentQuestion) ? 'secondary' : 'dark'}>
                      {currentQuestion.type === 'ranked_multi'
                        ? 'Ranked multi-select'
                        : currentQuestion.type === 'multi'
                          ? 'Multi-select'
                          : 'Single-select'}
                    </Badge>
                  </div>
                  <h3 className="h6 mb-2">{currentQuestion.prompt}</h3>
                  {currentQuestion.helper && <p className="small text-secondary mb-3">{currentQuestion.helper}</p>}
                  {currentQuestion.allowSelectAll && isMultiQuestion(currentQuestion) && (
                    <div className="d-flex justify-content-end mb-2">
                      {(() => {
                        const hasAllSelected = areAllSelectableOptionsSelected(
                          currentQuestion,
                          quizAnswers[currentQuestion.id],
                        );
                        const actionLabel = hasAllSelected ? 'Clear all' : 'Select all';
                        return (
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => toggleSelectAllAnswers(currentQuestion)}
                            aria-label={`${actionLabel} ${currentQuestion.label.toLowerCase()}`}
                          >
                            {actionLabel}
                          </Button>
                        );
                      })()}
                    </div>
                  )}
                  <div
                    className={currentQuestion.layout === 'grid' ? 'row g-2' : 'd-grid gap-2'}
                    role={isSingleQuestion ? 'radiogroup' : undefined}
                    aria-label={isSingleQuestion ? currentQuestion.label : undefined}
                  >
                    {currentQuestion.options.map((option) => {
                      const selected = isSelected(currentQuestion, quizAnswers, option.value);
                      const selectedRank = getSelectedRank(currentQuestion, quizAnswers, option.value);
                      const button = (
                        <Button
                          key={option.value}
                          type="button"
                          variant={optionButtonVariant(selected)}
                          className="text-start w-100"
                          role={isSingleQuestion ? 'radio' : undefined}
                          aria-checked={isSingleQuestion ? selected : undefined}
                          aria-pressed={isSingleQuestion ? undefined : selected}
                          aria-label={selectedRank ? `${option.label} selected as rank ${selectedRank}` : option.label}
                          onClick={() =>
                            currentQuestion.type === 'single'
                              ? updateSingleAnswer(currentQuestion, option.value)
                              : toggleMultiAnswer(currentQuestion, option.value, option.clearOthers)
                          }
                        >
                          <span className="d-flex align-items-center justify-content-between gap-2">
                            <span className="fw-semibold">{option.label}</span>
                            {selectedRank && (
                              <Badge bg="light" text="dark" className="border">
                                {selectedRank}
                              </Badge>
                            )}
                          </span>
                          {option.description && <span className="d-block small opacity-75">{option.description}</span>}
                        </Button>
                      );

                      if (currentQuestion.layout === 'grid') {
                        return (
                          <Col xs={6} key={option.value}>
                            {button}
                          </Col>
                        );
                      }

                      return button;
                    })}
                  </div>
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={goToPreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      variant="outline-dark"
                      size="sm"
                      onClick={goToNextQuestion}
                      disabled={currentQuestionIndex === skinMatchSurveyQuestions.length - 1}
                    >
                      Continue
                    </Button>
                  </div>
                </div>

                <h3 className="h6 mb-3">Hair signals</h3>
                <Form.Group controlId="hairType" className="mb-3">
                  <Form.Label>Hair type</Form.Label>
                  <Form.Select value={hairType} onChange={(event) => setHairType(event.currentTarget.value)}>
                    <option>Straight</option>
                    <option>Wavy</option>
                    <option>Curly</option>
                    <option>Coily</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group controlId="mainHairConcern" className="mb-3">
                  <Form.Label>Main hair concern</Form.Label>
                  <Form.Select
                    value={mainHairConcern}
                    onChange={(event) => setMainHairConcern(event.currentTarget.value)}
                  >
                    <option>Frizz</option>
                    <option>Damage</option>
                    <option>Volume</option>
                    <option>Oiliness</option>
                    <option>Dry scalp</option>
                  </Form.Select>
                </Form.Group>
                <Form.Check
                  id="coloredHair"
                  className="mb-2"
                  label="Colored hair"
                  checked={hasColoredHair}
                  onChange={(event) => setHasColoredHair(event.currentTarget.checked)}
                />
                <Form.Check
                  id="heatStyling"
                  label="Frequent heat styling"
                  checked={hasHeatStyling}
                  onChange={(event) => setHasHeatStyling(event.currentTarget.checked)}
                />
              </Card.Body>
            </Card>

            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
            {savedResponse && (
              <Alert variant="success" className="py-2">
                Survey response #{savedResponse.id} saved
              </Alert>
            )}
            <Button
              type="submit"
              disabled={isLoading || isSurveyLoading || !questionnaire?.latestVersion}
              className="w-100"
              variant="dark"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Running analysis
                </>
              ) : isSurveyLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Preparing survey
                </>
              ) : (
                'Run demo analysis'
              )}
            </Button>
          </Form>
        </Col>

        <Col xl={8}>
          <section className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                  <div>
                    <h2 className="h5 mb-1">Quiz answers</h2>
                    <p className="small text-secondary mb-0">These answers are sent with the image analysis request.</p>
                  </div>
                  <Badge bg="success">{answers.length} payload fields</Badge>
                </div>
                <Row className="g-2">
                  {skinMatchSurveyQuestions.map((question) => {
                    const values = getDisplayLabels(question, quizAnswers[question.id]);
                    const displayValues =
                      question.type === 'ranked_multi'
                        ? values.map((value, index) => `${index + 1}. ${value}`)
                        : values;
                    return (
                      <Col md={6} xl={4} key={question.id}>
                        <div className="border rounded-2 p-2 h-100 bg-white">
                          <div className="small text-secondary mb-1">{question.label}</div>
                          <div className="small fw-semibold">
                            {displayValues.length > 0 ? displayValues.join(', ') : 'Not answered'}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </section>

          <section className="mb-4">
            <h2 className="h5 mb-3">Photo guidance</h2>
            <Row className="g-3">
              {captureGuides.map((guide) => (
                <Col md={4} key={guide.id}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Img variant="top" src={guide.imageSrc} alt={guide.title} />
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <Card.Title className="h6 mb-0">{guide.title}</Card.Title>
                        <Badge bg={guide.badge === 'Required' ? 'danger' : 'secondary'}>{guide.badge}</Badge>
                      </div>
                      <Card.Text className="small text-secondary mb-0">{guide.description}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>

          {result ? (
            <>
              <section className="mb-4">
                <Row className="g-3">
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <Badge bg="light" text="dark" className="mb-2">
                          Skin profile
                        </Badge>
                        <h2 className="h5 mb-2">{result.skinProfile.skinType}</h2>
                        <p className="text-secondary mb-3">{result.skinProfile.summary}</p>
                        <Row className="g-3">
                          {result.skinProfile.concerns.map((concern) => (
                            <Col sm={6} key={concern.techName}>
                              <MetricCard label={concern.label} value={concern.value} score={concern.score} />
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <Badge bg="light" text="dark" className="mb-2">
                          Hair profile
                        </Badge>
                        <h2 className="h5 mb-2">{result.hairProfile.hairType}</h2>
                        <p className="text-secondary mb-3">{result.hairProfile.summary}</p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <Badge bg="secondary">{result.hairProfile.condition}</Badge>
                          {result.hairProfile.concerns.map((concern) => (
                            <Badge bg="outline-secondary" text="dark" className="border" key={concern}>
                              {concern}
                            </Badge>
                          ))}
                        </div>
                        <Row className="g-3">
                          {result.hairProfile.metrics.map((metric) => (
                            <Col sm={6} key={metric.techName}>
                              <MetricCard label={metric.label} value={metric.value} score={metric.confidence} />
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </section>

              <section className="mb-4">
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h2 className="h5 mb-3">Recommended routine</h2>
                    <RecommendationList title="Morning routine" items={result.recommendations.am} />
                    <RecommendationList title="Evening routine" items={result.recommendations.pm} />
                    <RecommendationList title="Weekly routine" items={result.recommendations.weekly} />
                    <RecommendationList title="Haircare routine" items={result.recommendations.hair} />
                  </Card.Body>
                </Card>
              </section>

              {hautPresentation.faceSkin && <HautFaceSkinResultSection faceSkin={hautPresentation.faceSkin} />}

              <details className="border rounded-2 p-3 bg-white">
                <summary className="fw-semibold">Raw demo response</summary>
                <pre className="small mt-3 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </>
          ) : (
            <Alert variant="light" className="border">
              Run an analysis to preview the customer-facing skin, hair, and product recommendation output.
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
}
